/**
 * Import Progress Dialog Component
 * Displays real-time progress for async translation imports with per-language tracking
 *
 * Supports two modes:
 * 1. Streaming mode (primary) - SSE streaming for both JSON and Excel imports, receives streamingStatus prop directly
 * 2. Polling mode (legacy fallback) - uses importId prop to poll for status
 */

import { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';
import { Button } from '@/core/components/ui/button';
import { Progress } from '@/core/components/ui/progress';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { Badge } from '@/core/components/ui/badge';
import { Loader2, CheckCircle, XCircle, AlertCircle, Circle, Clock } from 'lucide-react';
import { servicesContainer } from '@/core/di/services-container';
import {
  TranslationImportStatusResponse,
  ImportStatus,
  ImportFailedKey,
  LanguageImportResult,
  getLanguageName,
} from '../../types/translation-types';

interface ImportProgressDialogProps {
  open: boolean;
  /** Import ID for polling mode (legacy fallback) */
  importId?: string | null;
  /** Streaming status for SSE mode (both JSON and Excel imports) - passed directly from parent */
  streamingStatus?: TranslationImportStatusResponse | null;
  /** Whether streaming is in progress */
  isStreaming?: boolean;
  /** Error message from streaming */
  streamingError?: string | null;
  onClose: () => void;
  onComplete: (result: TranslationImportStatusResponse) => void;
  /** Optional cancel handler for streaming imports */
  onCancel?: () => void;
}

export function ImportProgressDialog({
  open,
  importId,
  streamingStatus,
  isStreaming = false,
  streamingError,
  onClose,
  onComplete,
  onCancel,
}: ImportProgressDialogProps) {
  // For polling mode, we manage status internally
  const [pollingStatus, setPollingStatus] = useState<TranslationImportStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  // Track if completion callback has been called to avoid duplicate calls
  const [completionHandled, setCompletionHandled] = useState(false);

  // Determine which mode we're in and which status to use
  const isStreamingMode = !importId && (isStreaming || streamingStatus !== undefined);
  const status = isStreamingMode ? streamingStatus : pollingStatus;
  const displayError = isStreamingMode ? streamingError : error;

  // Poll for import status (only used in polling mode)
  const pollStatus = useCallback(async () => {
    if (!importId) return;

    try {
      const services = servicesContainer.getServices();
      const statusResponse = await services.translationApi.getImportStatus(importId);

      // Check for API-level errors (e.g., "Import not found or expired")
      // This can happen with multi-instance servers - just keep polling until we get the right server
      if (!statusResponse.isSuccess && statusResponse.errorMessage) {
        console.warn('ImportProgressDialog.pollStatus: API returned error, continuing to poll:', statusResponse.errorMessage);
        return; // Skip this poll, keep previous status, continue polling
      }

      setPollingStatus(statusResponse);

      // Stop polling when completed or failed
      if (
        statusResponse.status === ImportStatus.Completed ||
        statusResponse.status === ImportStatus.Failed
      ) {
        setIsPolling(false);
      }
    } catch (err) {
      console.error('ImportProgressDialog.pollStatus: Failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to get import status');
      setIsPolling(false);
    }
  }, [importId]);

  // Start polling when dialog opens with importId (polling mode only)
  useEffect(() => {
    // Only poll if we have an importId (not in streaming mode)
    if (!importId || !open || isStreamingMode) {
      setIsPolling(false);
      return;
    }

    setIsPolling(true);
    setError(null);
    setPollingStatus(null);
    setCompletionHandled(false);

    // Initial poll
    pollStatus();

    // Poll every 500ms
    const interval = setInterval(pollStatus, 500);

    return () => {
      clearInterval(interval);
      setIsPolling(false);
    };
  }, [importId, open, pollStatus, isStreamingMode]);

  // Reset completion handled when dialog opens
  useEffect(() => {
    if (open) {
      setCompletionHandled(false);
    }
  }, [open]);

  // Handle completion separately to avoid callback dependency issues
  useEffect(() => {
    if (
      status &&
      !completionHandled &&
      (status.status === ImportStatus.Completed || status.status === ImportStatus.Failed)
    ) {
      setCompletionHandled(true);
      onComplete(status);
    }
  }, [status, completionHandled, onComplete]);

  const getStatusText = (): string => {
    if (displayError) return 'Error occurred';
    if (!status) return 'Starting import...';

    switch (status.status) {
      case ImportStatus.Pending:
        return 'Preparing import...';
      case ImportStatus.InProgress:
        if (status.currentLanguage && status.currentLanguageIndex && status.totalLanguages) {
          return `Processing: ${getLanguageName(status.currentLanguage)} (${status.currentLanguage.toUpperCase()}) - language ${status.currentLanguageIndex} of ${status.totalLanguages}`;
        }
        if (status.currentLanguage) {
          return `Processing language: ${getLanguageName(status.currentLanguage)} (${status.currentLanguage.toUpperCase()})`;
        }
        return `Processing ${status.processedKeys.toLocaleString()} of ${status.totalKeys.toLocaleString()} keys...`;
      case ImportStatus.Completed:
        return 'Import completed!';
      case ImportStatus.Failed:
        return status.errorMessage || 'Import failed';
      default:
        return 'Unknown status';
    }
  };

  const getStatusIcon = () => {
    if (displayError) return <XCircle className="h-6 w-6 text-red-500" />;
    if (!status) return <Loader2 className="h-6 w-6 animate-spin text-blue-500" />;

    switch (status.status) {
      case ImportStatus.Pending:
      case ImportStatus.InProgress:
        return <Loader2 className="h-6 w-6 animate-spin text-blue-500" />;
      case ImportStatus.Completed:
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case ImportStatus.Failed:
        return <XCircle className="h-6 w-6 text-red-500" />;
      default:
        return <AlertCircle className="h-6 w-6 text-gray-500" />;
    }
  };

  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;

    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);

    if (minutes > 0) {
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    }

    return `${seconds}s`;
  };

  const getLanguageStatusIcon = (langCode: string) => {
    // Check if this language is completed
    const langResult = status?.languageResults?.find(r => r.languageCode === langCode);

    if (langResult) {
      if (langResult.isSuccess) {
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      }
      return <XCircle className="h-4 w-4 text-red-500" />;
    }

    // Check if currently processing
    if (status?.currentLanguage === langCode) {
      return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
    }

    // Pending
    return <Circle className="h-4 w-4 text-gray-300" />;
  };

  // Can close when: not actively importing, or completed/failed, or has error
  const isActivelyImporting = isStreamingMode ? isStreaming : isPolling;
  const canClose = !isActivelyImporting || status?.status === ImportStatus.Completed || status?.status === ImportStatus.Failed || displayError !== null;

  // Check if we have language tracking data
  const hasLanguageTracking = status && status.totalLanguages > 0;

  // Get pending languages (not completed and not current)
  const getPendingLanguages = (): string[] => {
    if (!status?.languagesToProcess) return [];
    const completedCodes = new Set(status.languageResults?.map(r => r.languageCode) || []);
    return status.languagesToProcess.filter(
      lang => !completedCodes.has(lang) && lang !== status.currentLanguage
    );
  };

  // Handle dialog close/X button click
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      // User is trying to close the dialog
      if (canClose) {
        onClose();
      } else if (isStreamingMode && isStreaming && onCancel) {
        // During streaming, X button should cancel the import
        onCancel();
      } else if (!isStreamingMode && isPolling) {
        // During polling, allow closing (server-side import continues but we stop tracking)
        setIsPolling(false);
        onClose();
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px]" onPointerDownOutside={(e) => !canClose && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getStatusIcon()}
            Importing Translations
          </DialogTitle>
          <DialogDescription>
            {getStatusText()}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {displayError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{displayError}</AlertDescription>
            </Alert>
          ) : (
            <>
              {/* Overall Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-muted-foreground mb-1">
                  <span>Overall Progress</span>
                  <span>{status?.processedKeys?.toLocaleString() ?? 0} / {status?.totalKeys?.toLocaleString() ?? 0} keys</span>
                </div>
                <Progress value={status?.percentComplete ?? 0} className="h-3" />
                <div className="text-right text-sm text-muted-foreground">
                  {status?.percentComplete ?? 0}% complete
                </div>
              </div>

              {/* Duration (during import) */}
              {status?.status === ImportStatus.InProgress && status.durationMs > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>Elapsed: {formatDuration(status.durationMs)}</span>
                </div>
              )}

              {/* Language Progress List (during import) */}
              {hasLanguageTracking && status.status === ImportStatus.InProgress && (
                <div className="rounded-lg border bg-muted/20 p-3 space-y-3">
                  {/* Completed Languages */}
                  {status.languageResults && status.languageResults.length > 0 && (
                    <div className="space-y-1.5">
                      <h4 className="text-sm font-medium text-muted-foreground">Completed Languages</h4>
                      <div className="space-y-1 max-h-[120px] overflow-y-auto">
                        {status.languageResults.map((langResult) => (
                          <div
                            key={langResult.languageCode}
                            className="flex items-center justify-between text-sm py-1 px-2 rounded bg-green-50 dark:bg-green-950/30"
                          >
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="font-medium">
                                {getLanguageName(langResult.languageCode)} ({langResult.languageCode.toUpperCase()})
                              </span>
                            </div>
                            <span className="text-muted-foreground">
                              {langResult.valuesUpdated.toLocaleString()} values
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Currently Processing */}
                  {status.currentLanguage && (
                    <div className="space-y-1.5">
                      <h4 className="text-sm font-medium text-muted-foreground">Currently Processing</h4>
                      <div className="flex items-center justify-between text-sm py-1 px-2 rounded bg-blue-50 dark:bg-blue-950/30">
                        <div className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                          <span className="font-semibold text-blue-600 dark:text-blue-400">
                            {getLanguageName(status.currentLanguage)} ({status.currentLanguage.toUpperCase()})
                          </span>
                        </div>
                        <span className="text-blue-600 dark:text-blue-400">processing...</span>
                      </div>
                    </div>
                  )}

                  {/* Pending Languages */}
                  {getPendingLanguages().length > 0 && (
                    <div className="space-y-1.5">
                      <h4 className="text-sm font-medium text-muted-foreground">Pending</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {getPendingLanguages().map((langCode) => (
                          <Badge key={langCode} variant="outline" className="text-xs">
                            {langCode.toUpperCase()}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Completion Summary */}
              {status?.status === ImportStatus.Completed && (
                <div className="space-y-4">
                  {/* Overall Summary */}
                  <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
                    <h4 className="text-sm font-medium mb-2">Import Summary</h4>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Keys Added:</span>
                      <Badge variant="default" className="bg-green-500">
                        {status.keysAdded.toLocaleString()}
                      </Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Keys Updated:</span>
                      <Badge variant="default" className="bg-blue-500">
                        {status.keysUpdated.toLocaleString()}
                      </Badge>
                    </div>
                    {status.keysDeleted > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Keys Deleted:</span>
                        <Badge variant="default" className="bg-orange-500">
                          {status.keysDeleted.toLocaleString()}
                        </Badge>
                      </div>
                    )}
                    {status.keysFailed > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Keys Failed:</span>
                        <Badge variant="destructive">
                          {status.keysFailed.toLocaleString()}
                        </Badge>
                      </div>
                    )}
                    {status.valuesUpdated > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Total Values Updated:</span>
                        <span className="font-medium">{status.valuesUpdated.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm pt-2 border-t">
                      <span className="text-muted-foreground">Duration:</span>
                      <span className="font-medium">{formatDuration(status.durationMs)}</span>
                    </div>
                  </div>

                  {/* Per-Language Results Table */}
                  {hasLanguageTracking && status.languageResults && status.languageResults.length > 0 && (
                    <div className="rounded-lg border bg-muted/20 p-4 space-y-2">
                      <h4 className="text-sm font-medium mb-2">Results by Language</h4>
                      <div className="max-h-[200px] overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="border-b">
                            <tr className="text-muted-foreground">
                              <th className="text-left py-1 font-medium">Language</th>
                              <th className="text-right py-1 font-medium">Keys</th>
                              <th className="text-right py-1 font-medium">Values</th>
                              <th className="text-center py-1 font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {status.languageResults.map((langResult) => (
                              <tr key={langResult.languageCode} className="border-b border-muted/50 last:border-0">
                                <td className="py-1.5">
                                  <span className="font-medium">{getLanguageName(langResult.languageCode)}</span>
                                  <span className="text-muted-foreground ml-1">({langResult.languageCode.toUpperCase()})</span>
                                </td>
                                <td className="py-1.5 text-right">{langResult.keysProcessed.toLocaleString()}</td>
                                <td className="py-1.5 text-right">{langResult.valuesUpdated.toLocaleString()}</td>
                                <td className="py-1.5 text-center">
                                  {langResult.isSuccess ? (
                                    <CheckCircle className="h-4 w-4 text-green-500 inline-block" />
                                  ) : (
                                    <XCircle className="h-4 w-4 text-red-500 inline-block" title={langResult.errorMessage || undefined} />
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Failed Keys List */}
              {status?.failedKeys && status.failedKeys.length > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 p-4 space-y-2">
                  <h4 className="font-semibold text-sm text-red-800 dark:text-red-400">Failed Keys:</h4>
                  <div className="max-h-[200px] overflow-y-auto space-y-1">
                    {status.failedKeys.slice(0, 10).map((failedKey: ImportFailedKey, idx: number) => (
                      <div key={idx} className="text-xs text-red-700 dark:text-red-300">
                        <strong>{failedKey.keyName}</strong>
                        {failedKey.rowNumber && <span className="text-red-600 dark:text-red-400"> (Row {failedKey.rowNumber})</span>}
                        : {failedKey.errorMessage}
                      </div>
                    ))}
                    {status.failedKeys.length > 10 && (
                      <p className="text-xs text-red-600 dark:text-red-400 italic">
                        ...and {status.failedKeys.length - 10} more failed keys
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          {/* Show cancel button during streaming import */}
          {isStreamingMode && isStreaming && onCancel && (
            <Button variant="outline" onClick={onCancel}>
              Cancel Import
            </Button>
          )}
          <Button onClick={onClose} disabled={!canClose}>
            {canClose ? 'Close' : 'Importing...'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
